import React, {useEffect, useMemo, useState} from "react";
import { Button, Column, Columns, Control, Field, Modal, Section, Select, Title } from "@allxsmith/bestax-bulma";
import {AsyncTypeahead} from "react-bootstrap-typeahead";
import axios from "axios";
import {createSetDataFreshAction} from "../../actions/dataActions";
import {useDispatch, useSelector} from "react-redux";
import {getDataFresh} from "../../state/selectors";
import {ToastContainer, toast} from "react-toastify";
import useInput from "../../hooks/useInput";
import {
  intervalTypes,
  paymentTypes,
} from "./types";
import RecurringPaymentPreview from "./RecurringPaymentPreview";
import DepartmentSelector from "../Settings/Departments/DepartmentSelector";


export default function CreateRecurringPaymentModal({open, setOpen, endpoint, costCentres, costCentreObjects, departments, departmentObjects}) {
  const dispatch = useDispatch();
  const dataFresh = useSelector(getDataFresh);
  const [, setTypeahead] = useState();
  const [isLoading, setLoading] = useState(false);
  const [searchData, setSearchData] = useState([]);

  // Form data
  const [contact, setContact] = useState();
  const [paymentType, setPaymentType] = useState();
  const [intervalType, setIntervalType] = useState();

  const [startDate, startDateInput, setStartDate] = useInput({
    type: "date",
    label: "Start Date",
    initialValue: null,
  });

  const [endDate, endDateInput, setEndDate] = useInput({
    type: "date",
    label: "End Date",
    initialValue: null
  })

  const [paymentAmount, paymentAmountInput, setPaymentAmount] = useInput({
    type: "number",
    currency: true,
    label: "Payment Amount",
    initialValue: null
  });

  const [intervalDay, intervalDaysInput, setIntervalDay] = useInput({
    type: "number",
    label: "Every X Days",
    initialValue: null
  });
  const [costCentre, setCostCentre] = useState(costCentres[0]);
  const [department, setDepartment] = useState(departments[0])

  const [comments, commentsInput, setComments] = useInput({
    label: "Comments",
    type: "text",
    initialValue: null
  });

  const clearForm = () => {
    setContact(undefined);
    setPaymentType(undefined);
    setIntervalType(undefined);
    setIntervalDay(undefined);
    setStartDate(undefined);
    setEndDate(undefined);
    setPaymentAmount(undefined);
    setComments(undefined);
    setCostCentre(costCentres[0]);
    setDepartment(departments[0])
  }
  const onCCChange = (e) => {
    setCostCentre(costCentreObjects[e.target.value])
  };
  const onDepartmentChange = e => {
    setDepartment(departmentObjects[e.target.value]);
  }
  // Reset interval day when interval type changes
  useEffect(() => {
    setIntervalDay(null);
  }, [intervalType]);

  const checkFieldValidation = () => {
    // startDate
    const startDateDate = new Date(startDate);
    const todayDateString = new Date().toISOString().slice(0, 10);
    const today = new Date(todayDateString);
    if (startDateDate < today) {
      toast.error("Minimum start date is today", {position: "top-right"});
      setStartDate(todayDateString);
      return false;
    }

    // intervalDay
    if (intervalDay && intervalDay < 7) {
      toast.error("Minimum payment interval is 7 days", {position: "top-right"});
      setIntervalDay(7);
      return false;
    }

    return true;
  }

  const onClickAction = () => {
    if (checkFieldValidation() && checkAllFieldsFilled()) {
      save();
      clearForm();
      setOpen(false);
    } else {
      toast.error("Please fill in all fields", {position: "top-right"});
    }
  };

  const handleContactSearch = e => {
    setLoading(true);
    axios.get(`/api/contacts?search=${e}`).then(resp => {
      setSearchData(resp.data.results.map(contact => ({
        ...contact,
        display_name: `${contact.display_name}`
      })));
      setLoading(false);
    });
  }

  const handleContactSelect = contacts => {
    setContact(contacts[0]);
  };

  const checkAllFieldsFilled = () => {
    if (!contact || !startDate || !paymentType || !intervalType || !paymentAmount || !department) {
      return false;
    }

    if (intervalType === 'FIXED_DURATION' && !intervalDay) {
      return false;
    }

    return true;
  }

  const showPreview = useMemo(() => {
    if (intervalType === 'FIXED_DURATION') {
      return !!startDate && !!intervalType && !!intervalDay
    }
    return !!startDate && !!intervalType
  }, [intervalType, intervalDay, startDate]);

  const handlePaymentTypeUpdate = (e) => {
    setPaymentType(e.target.value);
  }

  const handleIntervalTypeUpdate = (e) => {
    setIntervalType(e.target.value);
  }

  // Save an existing tracker
  const save = () => {
    const url = endpoint + "recurringpayments";
    const data = {
      contact,
      start_date: startDate,
      end_date: endDate,
      interval_payment_amount: Number(paymentAmount),
      payment_type: paymentType,
      interval_type: intervalType,
      interval_day: Number(intervalDay),
      cost_centre: costCentre,
      comments: comments,
      department: department
    };

    const conf = {
      method: "post",
      data,
      url
    };

    return axios(conf).then(() => {
      dispatch(createSetDataFreshAction(dataFresh + 1));
      setOpen(false);
    });
  };

  // TODO(bestax-migrate): `closeOnBlur` — bestax Modal background click closes when onClose is set; remove
  return (
    <Modal
      closeOnBlur
      active={open}
      nClick={e => {
        e.stopPropagation()
      }}
      onClose={() => {
        setOpen(false);
      }}
    >
      <Modal.Content className="wider-modal">
        <Section style={{backgroundColor: "white"}}>
          <Title className="is-centered">Create New Recurring Payment</Title>
          <p className="is-centered">* Leave end date blank for recurring payments that repeat indefinitely<br/><br/>
          </p>
          <Columns isMultiline>
            <Column>
              <Field>
                <label className="label">Contact</label>
                <Control>
                  <AsyncTypeahead
                    useCache={false}
                    id="typeahead"
                    labelKey="display_name"
                    minLength={2}
                    name={"contact"}
                    onSearch={handleContactSearch}
                    onChange={handleContactSelect}
                    placeholder={"Search Contacts"}
                    options={searchData}
                    ref={typeahead => setTypeahead(typeahead)}
                    className="typeahead"
                    isLoading={isLoading}
                    selected={contact && contact.id ? [contact] : []}
                  />
                </Control>
              </Field>
            </Column>
            <Column>
              <DepartmentSelector
                name={"department"}
                value={department}
                onChange={onDepartmentChange}
                options={departments}
                editable={true}
                inlineEdit={false}
                disabled={false}
                blank={true}
                label={"Department"}
              />
            </Column>
            <Column>
              <Field>
                <Control>
                  <label className="label">Payment Type</label>
                  <Select
                    onChange={handlePaymentTypeUpdate}
                    name="payment_type"
                    value={paymentType}
                  >
                    <option key={-1} value={undefined}></option>
                    {paymentTypes.map(value =>
                      <option key={value.id} value={value.id}>
                        {value.name}
                      </option>
                    )}
                  </Select>
                </Control>
              </Field>
            </Column>
            {startDateInput}
            {endDateInput}
          </Columns>
          <Columns isMultiline>
            <Column>
              <Columns isMultiline>
                <Column>
                  <Field>
                    <Control>
                      <label className="label">Interval Type</label>
                      <Select
                        onChange={handleIntervalTypeUpdate}
                        name="interval_type"
                        value={intervalType}
                      >
                        <option key={-1} value={undefined}></option>
                        {intervalTypes.map(value =>
                          <option key={value.id} value={value.id}>
                            {value.name}
                          </option>
                        )}
                      </Select>
                    </Control>
                  </Field>
                </Column>
                {intervalType && intervalType === 'FIXED_DURATION' && intervalDaysInput}
              </Columns>
            </Column>
            <Column>
              <td>
                <Control>
                  <label className="label">Cost Centre</label>
                  <Select
                      name="cost_centre"
                      value={costCentre ? costCentre.value : costCentres[0].value}
                      onChange={e => onCCChange(e)}
                  >
                    {costCentres.map(value => (
                        <option key={value.id} value={value.id}>
                          {value.name}
                        </option>
                    ))}
                  </Select>
                </Control>
              </td>
            </Column>
            {paymentAmountInput}
            {commentsInput}
            <Column>
              {
                // Extra column to make both rows equal length
              }
            </Column>
          </Columns>
          {
            showPreview &&
            <RecurringPaymentPreview
              startDate={startDate}
              endDate={endDate}
              intervalType={intervalType}
              intervalDays={intervalDay}
              paymentAmount={paymentAmount}
              maxRows={5}
            />
          }
          <Columns isMultiline>
            <Column>
              <Button
                color="info"
                onClick={() => {
                  clearForm();
                  setOpen(false);
                }}
                isFullWidth
              >
                Cancel
              </Button>
            </Column>
            <Column>
              <Button color="danger" onClick={onClickAction} isFullWidth>
                Save Recurring Payment
              </Button>
            </Column>
          </Columns>
        </Section>
      </Modal.Content>
    </Modal>
  );
}
